import React, { useState, useEffect } from "react";
import { useWebSocketContext } from "../../contexts/WebSocketContext";
import { NotesLockUpdatedData } from "../../types/websocket";
import { sessionService } from "../../services/sessionService";

interface DMToolsPanelProps {
  className?: string;
  isDM: boolean;
  sessionId?: string;
}

export const DMToolsPanel: React.FC<DMToolsPanelProps> = ({ className = "", isDM, sessionId }) => {
  const [activeTab, setActiveTab] = useState<"tools" | "assets" | "notes">("tools");
  const [sessionNotes, setSessionNotes] = useState("");
  const [showNPCGenerator, setShowNPCGenerator] = useState(false);
  const [npcName, setNpcName] = useState("");
  const [npcRole, setNpcRole] = useState("");
  const [npcDescription, setNpcDescription] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  const { isConnected, sendSessionControl, updateCombatState, lastMessage } = useWebSocketContext();

  // Load the initial lock state from session details so the toggle shows
  // the correct state immediately on mount (before any WS lock message arrives).
  useEffect(() => {
    if (!sessionId) return;
    sessionService
      .getSessionDetails(sessionId)
      .then((details) => {
        if (details.are_notes_locked !== undefined) {
          setIsLocked(details.are_notes_locked);
        }
      })
      .catch((e) => console.error("DMToolsPanel: failed to load initial lock state", e));
  }, [sessionId]);

  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type !== "notes_lock_updated") return;
    if (String(lastMessage.sessionId) !== String(sessionId)) return;

    const data = lastMessage.data as NotesLockUpdatedData;
    setIsLocked(data.isLocked);
  }, [lastMessage, sessionId]);

  if (!isDM) {
    return null; // Only show for DMs
  }

  const tabs = [
    { id: "tools", label: "Tools", icon: "🎲" },
    { id: "assets", label: "Assets", icon: "📚" },
    { id: "notes", label: "Notes", icon: "📝" },
  ];

  const handleAction = (action: string) => {
    if (!sessionId || !isConnected) {
      console.warn("Cannot perform action: session not connected");
      return;
    }

    switch (action) {
      case "initiative":
        // Start combat
        updateCombatState(sessionId, "active");
        break;
      case "npc":
        setShowNPCGenerator(true);
        break;
      case "pause":
        sendSessionControl(sessionId, "pause");
        break;
      case "save":
        // TODO: Implement save functionality
        console.log("Save session");
        break;
      case "lock_notes":
        sendSessionControl(sessionId, "lock_notes");
        break;
      case "unlock_notes":
        sendSessionControl(sessionId, "unlock_notes");
        break;
      default:
        console.log(`DM Action: ${action}`);
    }
  };

  const generateNPC = () => {
    // Simple NPC generation logic
    const roles = ["Merchant", "Guard", "Innkeeper", "Scholar", "Blacksmith", "Priest"];
    const names = ["Aldric", "Brenna", "Corwin", "Desta", "Eldon", "Fiona", "Gareth", "Helena"];

    if (!npcName) {
      setNpcName(names[Math.floor(Math.random() * names.length)]);
    }
    if (!npcRole) {
      setNpcRole(roles[Math.floor(Math.random() * roles.length)]);
    }
    if (!npcDescription) {
      setNpcDescription(`A ${npcRole || "mysterious"} figure with a story to tell.`);
    }
  };

  const saveNPC = () => {
    // TODO: Implement NPC saving to backend
    console.log("Saving NPC:", { npcName, npcRole, npcDescription });

    // Reset form
    setNpcName("");
    setNpcRole("");
    setNpcDescription("");
    setShowNPCGenerator(false);
  };

  const quickActions = [
    { label: "Roll Initiative", action: "initiative", color: "bg-red-600 hover:bg-red-700" },
    { label: "Add NPC", action: "npc", color: "bg-green-600 hover:bg-green-700" },
    { label: "Pause Session", action: "pause", color: "bg-yellow-600 hover:bg-yellow-700" },
    { label: "Save Session", action: "save", color: "bg-indigo-600 hover:bg-indigo-700" },
  ];

  return (
    <div
      className={`bg-gray-800 rounded-lg border border-gray-600 ${className}`}
      data-testid="dm-tools-panel"
      role="region"
      aria-label="Dungeon Master tools and controls"
    >
      {/* Tab Navigation */}
      <div className="flex border-b border-gray-600" data-testid="dm-tools-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as "tools" | "assets" | "notes")}
            className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "text-white bg-gray-700 border-b-2 border-blue-500"
                : "text-gray-400 hover:text-white hover:bg-gray-750"
            }`}
            data-testid={`dm-tools-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            role="tab"
          >
            <span className="mr-1">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-4" data-testid="dm-tools-content">
        {activeTab === "tools" && (
          <div className="space-y-4" data-testid="dm-tools-tools-tab">
            <div>
              <h4 className="text-white font-semibold mb-3">Quick Actions</h4>
              <div className="grid grid-cols-2 gap-2" data-testid="dm-tools-quick-actions">
                {quickActions.map((action) => (
                  <button
                    key={action.action}
                    onClick={() => handleAction(action.action)}
                    className={`px-3 py-2 text-white rounded text-sm font-medium transition-colors ${action.color}`}
                    data-testid={`dm-action-${action.action}`}
                    aria-label={`Quick action: ${action.label}`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-600 pt-4" data-testid="dm-session-controls">
              <h4 className="text-white font-semibold mb-3">Session Controls</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Background Music</span>
                  <button
                    className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                    data-testid="select-music-button"
                    aria-label="Select background music"
                  >
                    Select
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Ambient Sounds</span>
                  <button
                    className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-500"
                    data-testid="configure-sounds-button"
                    aria-label="Configure ambient sounds"
                  >
                    Configure
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Lighting</span>
                  <select
                    className="px-2 py-1 bg-gray-600 text-white rounded text-xs"
                    data-testid="lighting-select"
                    aria-label="Select lighting level"
                  >
                    <option>Bright</option>
                    <option>Dim</option>
                    <option>Dark</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Session Notes</span>
                  <button
                    onClick={() => handleAction(isLocked ? "unlock_notes" : "lock_notes")}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      isLocked
                        ? "bg-amber-600 hover:bg-amber-700 text-white"
                        : "bg-gray-600 hover:bg-gray-500 text-white"
                    }`}
                    data-testid="toggle-notes-lock-button"
                    aria-label={isLocked ? "Unlock session notes" : "Lock session notes"}
                  >
                    {isLocked ? "🔓 Unlock" : "🔒 Lock"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "assets" && (
          <div className="space-y-4" data-testid="dm-tools-assets-tab">
            <div>
              <h4 className="text-white font-semibold mb-3">Chronicle Entries</h4>
              <div className="space-y-2" data-testid="campaign-assets-list">
                <div
                  className="p-2 bg-gray-700 rounded hover:bg-gray-650 cursor-pointer"
                  data-testid="asset-monster-manual"
                  role="button"
                  tabIndex={0}
                  aria-label="Monster Manual - 157 entries"
                >
                  <div className="text-white text-sm font-medium">Monster Manual</div>
                  <div className="text-gray-400 text-xs">157 entries</div>
                </div>
                <div
                  className="p-2 bg-gray-700 rounded hover:bg-gray-650 cursor-pointer"
                  data-testid="asset-world-map"
                  role="button"
                  tabIndex={0}
                  aria-label="World Map - Detailed region map"
                >
                  <div className="text-white text-sm font-medium">World Map</div>
                  <div className="text-gray-400 text-xs">Detailed region map</div>
                </div>
                <div
                  className="p-2 bg-gray-700 rounded hover:bg-gray-650 cursor-pointer"
                  data-testid="asset-npc-portraits"
                  role="button"
                  tabIndex={0}
                  aria-label="NPC Gallery - 42 characters"
                >
                  <div className="text-white text-sm font-medium">NPC Gallery</div>
                  <div className="text-gray-400 text-xs">42 characters</div>
                </div>
              </div>
            </div>

            <button
              className="w-full px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
              data-testid="browse-assets-button"
              aria-label="Browse all campaign assets"
            >
              Browse All Assets
            </button>
          </div>
        )}

        {activeTab === "notes" && (
          <div className="space-y-4" data-testid="dm-tools-notes-tab">
            <div>
              <h4 className="text-white font-semibold mb-3">Session Notes</h4>
              <textarea
                placeholder="Add session notes..."
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                className="w-full h-32 p-2 bg-gray-700 border border-gray-600 rounded text-white text-sm resize-none focus:border-blue-500 focus:outline-none"
                data-testid="session-notes-textarea"
                aria-label="Session notes text area"
              />
            </div>

            <button
              onClick={() => {
                console.log("Saving notes:", sessionNotes);
                // TODO: Implement save to backend
              }}
              className="w-full px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
              data-testid="save-notes-button"
              aria-label="Save session notes"
            >
              Save Notes
            </button>
          </div>
        )}
      </div>

      {/* NPC Generator Dialog */}
      {showNPCGenerator && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-white font-semibold text-lg mb-4">Quick NPC Generator</h3>
            <div className="space-y-3">
              <div>
                <label className="text-gray-300 text-sm block mb-1">Name</label>
                <input
                  type="text"
                  value={npcName}
                  onChange={(e) => setNpcName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="NPC name"
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm block mb-1">Role</label>
                <input
                  type="text"
                  value={npcRole}
                  onChange={(e) => setNpcRole(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none"
                  placeholder="e.g., Merchant, Guard, etc."
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm block mb-1">Description</label>
                <textarea
                  value={npcDescription}
                  onChange={(e) => setNpcDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-blue-500 focus:outline-none h-24 resize-none"
                  placeholder="Brief description"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-6">
              <button
                onClick={generateNPC}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
              >
                Generate Random
              </button>
              <button
                onClick={saveNPC}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Save NPC
              </button>
              <button
                onClick={() => setShowNPCGenerator(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
